import { isNone } from '@ember/utils';
import CampaignModel, { Edge } from 'cio/models/campaign';
import { WorkflowGraph, ActionWorkflowGraphNode } from 'cio/utils/visual-workflow-builder/workflow-graph';
import { removeNodeFromGraph, retrieveOutgoingEdges, isSameEdge } from 'cio/utils/visual-workflow-builder/branching';
import { DropType } from './types';
import DS from 'ember-data';
import ActionModel from 'cio/models/action';
import { expandTraversalOrder, getActionIdsInConsolidatedGroup } from '../campaigns/tree-consolidation';

export function prepareInsertAtEdges(dropTargetItem: any, dropType: DropType, campaign: CampaignModel) {
  let insertAtEdges: Edge[] = [];

  if (dropType === 'insertBefore') {
    let edgesIncomingToInsertBeforeAction = campaign.consolidatedTree.edges.filter(
      (edge) => edge.to === dropTargetItem.id,
    );
    insertAtEdges = edgesIncomingToInsertBeforeAction;

    if (insertAtEdges.length === 0) {
      // If this is a new event triggered campaign (with no
      // filter_match_delay action), there will be no existing edges yet.
      // Add a fake edge so that "addNodeToGraph" has a valid target to use.
      insertAtEdges = [{ to: dropTargetItem.id } as any];
    }
  } else if (dropType === 'insertAfter') {
    let edgeLeavingInsertAfterAction = campaign.consolidatedTree.edges.find(
      (edge) => edge.from === dropTargetItem.id,
    ) as Edge;
    insertAtEdges = [edgeLeavingInsertAfterAction];
  } else if (dropType === 'insertAtEdge') {
    insertAtEdges = [dropTargetItem];
  } else if (dropType === 'insertAtBranchEnd') {
    let rejoinPointId = campaign.branchRejoinPointsMap[dropTargetItem.id];

    insertAtEdges = retrieveOutgoingEdges(dropTargetItem.action, {
      edges: campaign.consolidatedTree.edges,
      reconnectNodeId: rejoinPointId,
    }).outgoingEdges;
  }

  return insertAtEdges;
}

export function connectNodesIntoSubGraph(sourceEdges: Edge[], nodeIds: string[]) {
  // Find all edges that point to or from the highlighted nodes
  let relevantEdges = sourceEdges
    .filter((e) => nodeIds.includes(e.from) || nodeIds.includes(e.to))
    .map((e) => ({ ...e }));

  let edgesPointingFromNonhighlightedActions = relevantEdges.filter((e) => !nodeIds.includes(e.from));
  let disconnectedStartActionIds = edgesPointingFromNonhighlightedActions.map((e) => e.to);

  // It's possible we're including items from the very beginning of a workflow
  // in the copy, meaning that an action may not have an incoming edge. Let's
  // add that action as an entrypoint too.
  disconnectedStartActionIds.push(...nodeIds.filter((id) => isNone(relevantEdges.find((e) => e.to === id))));

  disconnectedStartActionIds = disconnectedStartActionIds.uniq();

  let chains: { start: string; traversedIds: string[] }[] = [];

  function traverseEntryPoint(pointId: string) {
    let traversedIds: string[] = [];

    let stack: string[] = [pointId];

    while (stack.length > 0) {
      // we need to cast "as string" because TS doesn't realize that the pop
      // here will always produce a valid value (because we're in the while
      // loop).
      // https://github.com/microsoft/TypeScript/issues/30406
      let nodeId = stack.pop() as string;

      traversedIds.push(nodeId);

      let targets = relevantEdges
        .filter((e) => e.from === nodeId)
        .map((e) => e.to)
        .uniq();

      // - nodeIds.includes(t) - only consider nodes in the highlighted list of
      //   nodes
      // - !disconnectedStartActionIds.includes(t) - don't traverse nodes that
      //   are being pointed to from outside the highlight (this shouldn't
      //   happen during regular traversal anyway)
      // - !traversedIds.includes(t) - don't traverse nodes that we've already
      //   seen. If a node has been traversed, everything after it has been as
      //   well
      let copyTargets = targets.filter(
        (t) => nodeIds.includes(t) && !disconnectedStartActionIds.includes(t) && !traversedIds.includes(t),
      );

      stack.push(...copyTargets);
    }

    return {
      start: pointId,
      traversedIds,
    };
  }

  disconnectedStartActionIds.forEach((id) => {
    chains.push(traverseEntryPoint(id));
  });

  if (chains.length === 0) {
    return [];
  }

  let newEdges: Edge[] = [
    {
      from: 'IN',
      to: chains[0].start,
      type: 'continue',
    },
  ];

  chains.forEach((chain, index) => {
    let target = '';

    if (index === chains.length - 1) {
      target = 'OUT';
    } else {
      target = chains[index + 1].start;
    }

    let edges = relevantEdges
      .filter((e) => chain.traversedIds.includes(e.from))
      .map((e) => {
        let clone = { ...e };

        if (!nodeIds.includes(clone.to) || !chain.traversedIds.includes(clone.to)) {
          clone.to = target;
        }

        return clone;
      });

    newEdges.push(...edges);
  });

  return newEdges;
}

export function prepareExpandedEdgesForBulkCopy(
  sourceCampaign: CampaignModel,
  targetCampaign: CampaignModel,
  consolidatedHighlightedNodeIds: string[],
  consolidatedInsertAtEdges: Edge[],
  store: DS.Store,
) {
  let {
    parentConsolidatedBranchMap: sourceParentConsolidatedBranchMap,
    consolidatedActionsGroups: sourceConsolidatedActionsGroups,
  } = sourceCampaign;

  let {
    parentConsolidatedBranchMap: targetParentConsolidatedBranchMap,
    consolidatedActionsGroups: targetConsolidatedActionsGroups,
  } = targetCampaign;

  let highlightedNodeIds = [...consolidatedHighlightedNodeIds];

  // expand highlighted nodes
  for (let i = highlightedNodeIds.length - 1; i >= 0; i--) {
    let nodeId = highlightedNodeIds[i];
    if (sourceParentConsolidatedBranchMap[nodeId] != null) {
      // plop in entire group of action IDs from consolidated group
      let actionIds = getActionIdsInConsolidatedGroup(
        nodeId,
        sourceConsolidatedActionsGroups,
        sourceParentConsolidatedBranchMap,
      );
      highlightedNodeIds.splice(i, 1, ...actionIds);
    }
  }

  // expand insert edges
  let additionalInsertAtEdges: Edge[] = [];
  consolidatedInsertAtEdges.forEach((edge) => {
    if (targetParentConsolidatedBranchMap[edge.to] != null) {
      // reroute edge to parent of consolidate group
      edge.to = targetParentConsolidatedBranchMap[edge.to];
    }

    if (targetParentConsolidatedBranchMap[edge.from] != null) {
      // add additional edges for other sibling branches
      let group = targetConsolidatedActionsGroups[targetParentConsolidatedBranchMap[edge.from]];
      group.branchedActionIds.forEach((action) => {
        if (action !== edge.from) {
          additionalInsertAtEdges.push({ ...edge, from: action });
        }
      });
    }
  });

  // sort source edges by expanded traversal order
  let allIdsByTraversalOrder = sourceCampaign.traversedActions.map((a) => a.id);
  allIdsByTraversalOrder = expandTraversalOrder(
    allIdsByTraversalOrder,
    sourceConsolidatedActionsGroups,
    sourceParentConsolidatedBranchMap,
  );

  // We sort edge original edges according to traversal order, so that the
  // outcome of the move also flows according to the original order
  let sortedSourceEdges = [...sourceCampaign.edges].sort((a, b) => {
    return allIdsByTraversalOrder.indexOf(a.to) - allIdsByTraversalOrder.indexOf(b.to);
  });

  return prepareEdgesForBulkCopy(
    sortedSourceEdges,
    targetCampaign.edges,
    highlightedNodeIds,
    additionalInsertAtEdges.concat(consolidatedInsertAtEdges),
    store,
    true,
  );
}

export function prepareEdgesForBulkCopy(
  sourceEdges: Edge[],
  targetEdges: Edge[],
  highlightedNodeIds: string[],
  insertAtEdges: Edge[],
  store: DS.Store,
  reassignIds = false,
) {
  let actionModels = highlightedNodeIds.map((id) => store.peekRecord('action', id)) as ActionModel[];
  let serializedActions = actionModels.map((actionModel) => actionModel.serialize({ includeId: true }));

  let idMap: Record<string, string> = {};
  let reverseIdMap: Record<string, string> = {};

  serializedActions.forEach((a: any, index: number) => {
    let oldId = a.id;
    if (reassignIds) {
      a.id = `${index - serializedActions.length}`;
    } else {
      a.id = `-${a.id}`;
    }
    idMap[oldId] = a.id;
    reverseIdMap[a.id] = oldId;
  });

  let newEdges = connectNodesIntoSubGraph(sourceEdges, highlightedNodeIds);

  newEdges.forEach((e) => {
    if (idMap[e.to]) {
      e.to = idMap[e.to];
    }
    if (idMap[e.from]) {
      e.from = idMap[e.from];
    }
  });

  // all insertAtEdges always point "to" the same id
  let targetId = insertAtEdges[0].to;
  newEdges.forEach((edge) => {
    // Point the new outgoing edges to the target we're inserting at
    if (edge.to === 'OUT') {
      edge.to = targetId;
    }
  });

  let startNewEdge = newEdges.find((edge) => edge.from === 'IN');
  newEdges = newEdges.filter((edge) => edge.from !== 'IN');

  // For date and event-triggered campaigns there will be no filter_match_delay
  // action at the beginning, and prepareInsertAtEdges would've returned a
  // "fake" edge without a "from" (just so we can keep a reference of where
  // things are inserted).
  // In these steps we don't need that edge (in fact, keeping it can break
  // things), so we filter it out.
  let onlyRealInsertAtEdges = [...insertAtEdges.filter((edge) => !isNone(edge.from))];

  newEdges = [
    ...onlyRealInsertAtEdges.map((edge) => {
      if (!startNewEdge) {
        return edge;
      }

      return {
        ...edge,
        to: startNewEdge.to,
      };
    }),
    ...newEdges,
  ];

  if (!reassignIds) {
    [...newEdges, ...onlyRealInsertAtEdges].forEach((edge) => {
      if (edge.from.includes('-')) {
        edge.from = edge.from.replace('-', '');
      }

      if (edge.to.includes('-')) {
        edge.to = edge.to.replace('-', '');
      }
    });

    serializedActions.forEach((a: any) => {
      if (a.id.includes('-')) {
        a.id = a.id.replace('-', '');
      }
    });
  }

  let targetEdgesWithoutInsertAtEdges = targetEdges.filter((edge) => !insertAtEdges.some((e) => isSameEdge(e, edge)));

  return {
    newEdges: [...targetEdgesWithoutInsertAtEdges, ...newEdges],
    insertAtEdges,
    originalIdMap: reverseIdMap,
    serializedActions,
  };
}

export function bulkDelete(
  idsToDelete: string[],
  campaign: CampaignModel,
  workflowGraph: WorkflowGraph,
  edgesToUpdate: Edge[] = [],
) {
  if (!idsToDelete || idsToDelete.length === 0) {
    return;
  }

  let highlightedActions = idsToDelete.map((id) => (workflowGraph.nodes[id] as ActionWorkflowGraphNode).action);
  let campaignEdges = [...campaign.consolidatedTree.edges];

  let { actionDepths } = campaign;

  let rejoinPointsMap = { ...campaign.branchRejoinPointsMap };

  // Branch actions can only be bulk-selected as a whole. As such, deleting a
  // branch action also removes all its contents, so we'll start the
  // multi-delete by removing all the branches, starting from the top-most.
  // If a branch has nested branches, those will be removed in the same go as
  // their parent branch, and we won't need to call the removeNodeFromGraph
  // function again.
  highlightedActions = highlightedActions.sort((a, b) => {
    if (a.isBranch && b.isBranch) {
      return actionDepths[b.id] - actionDepths[a.id];
    }

    if (a.isBranch && !b.isBranch) {
      return -1;
    }

    if (!a.isBranch && b.isBranch) {
      return 1;
    }

    return 0;
  });

  let removedNodeIds: string[] = [];

  highlightedActions.forEach((a) => {
    if (removedNodeIds.includes(a.id)) {
      return;
    }

    let rejoinPoint = a.isBranch ? rejoinPointsMap[a.id] : null;

    let result = removeNodeFromGraph(a, campaignEdges, edgesToUpdate, rejoinPoint);
    removedNodeIds.push(a.id);
    if (a.isBranch) {
      removedNodeIds.push(...a.subActions);
    }
    edgesToUpdate = result.edgesToUpdate;

    // If the action we've just removed is a rejoint point of another branch,
    // update the rejoin points map to point to where the removed action pointed
    // before
    Object.keys(rejoinPointsMap).forEach((key) => {
      if (rejoinPointsMap[key] === a.id) {
        let outgoingEdge = campaignEdges.find((e) => e.from === a.id);
        if (outgoingEdge) {
          rejoinPointsMap[key] = outgoingEdge.to;
        }
      }
    });

    campaignEdges = result.edges;
  });

  return {
    newEdges: campaignEdges,
    edgesToUpdate,
    rejoinPointsMap,
  };
}
